import type { GridApi, GridRowId, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import { GRID_ROOT_GROUP_ID, gridRowTreeSelector } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { bumpVisibleItemsCache } from '../context/VisibleItemsContext';
import {
  type RelativePriority,
  reorderInCache,
  useItemRelativePriorityUpdate,
} from './useItemRelativePriorityUpdate';

export const useItemPriorityReorder = (apiRef: React.RefObject<GridApi>, instanceKey: string) => {
  const queryClient = useQueryClient();
  const updateRelativePriority = useItemRelativePriorityUpdate();

  return React.useCallback(
    async (params: GridRowOrderChangeParams) => {
      const sourceId: GridRowId = params.row.id;
      const movedId = Number(sourceId);
      const tree = gridRowTreeSelector(apiRef);
      const parentId = params.newParent ?? GRID_ROOT_GROUP_ID;
      const parentNode = tree[parentId];
      if (!parentNode || !('children' in parentNode)) return;

      const { children } = parentNode;
      const idx = params.targetIndex;
      const previousItemId = idx > 0 ? Number(children[idx - 1]) : undefined;
      const nextItemId = idx < children.length - 1 ? Number(children[idx + 1]) : undefined;

      // Moving up, the previous sibling already preceded this row — anchor on the next one instead.
      const movedUp = params.newParent === params.oldParent && params.targetIndex < params.oldIndex;

      let relativePriority: RelativePriority | undefined;
      if (movedUp && nextItemId !== undefined) {
        relativePriority = { id: nextItemId, type: 'before' };
      } else if (previousItemId !== undefined) {
        relativePriority = { id: previousItemId, type: 'after' };
      } else if (nextItemId !== undefined) {
        relativePriority = { id: nextItemId, type: 'before' };
      } else {
        return;
      }

      const { oldParent, oldIndex } = params;
      const restoreGridPosition = () => {
        const api = apiRef.current;
        if (!api) return;

        const oldParentNode = gridRowTreeSelector(apiRef)[oldParent ?? GRID_ROOT_GROUP_ID];
        if (!oldParentNode || !('children' in oldParentNode)) return;

        const siblings = oldParentNode.children.filter((id) => String(id) !== String(sourceId));
        const anchor = oldIndex === 0 ? siblings[0] : siblings[oldIndex - 1];
        const position = oldIndex === 0 ? 'above' : 'below';

        if (anchor === undefined) return;

        api.setRowPosition(sourceId, anchor, position);
        reorderInCache(queryClient, movedId, Number(anchor), position);
        bumpVisibleItemsCache(queryClient, instanceKey);
      };

      await updateRelativePriority(movedId, relativePriority, restoreGridPosition);
    },
    [apiRef, queryClient, instanceKey, updateRelativePriority],
  );
};
